import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiErrorMessage,
  cleanupAdminWcoDuplicates,
  getPlaceDetails,
  listAdminCollections,
  listAdminShares,
  listAdminWco,
  listAdminWcoDuplicates,
  markAdminWcoPlaceClosed,
  normalizeResponseData,
  updateAdminCollection,
  updateAdminShare,
  updateAdminWcoBulk,
  updateAdminWco
} from "../../core/api/adminApi";
import { useAuth } from "../../core/auth/AuthContext";
import type {
  AdminPlaceDetails,
  AdminCollectionItem,
  AdminCollectionListPayload,
  AdminShareItem,
  AdminShareListPayload,
  AdminWcoDuplicatePreviewPayload,
  AdminWcoItem,
  AdminWcoListPayload
} from "../../shared/types/admin";

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function parseTypes(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => !!entry)
    .filter((entry, index, arr) => arr.indexOf(entry) === index);
}

function parsePlaceIds(value: string): string[] {
  return value
    .split(/[\n,]+/g)
    .map((entry) => entry.trim())
    .filter((entry) => !!entry)
    .filter((entry, index, arr) => arr.indexOf(entry) === index);
}

function toDateTimeLocalInput(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDateTimeLocalInput(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export type PlacesMode = "all" | "wecommendations" | "collections" | "shares";

export function PlacesPage({ mode = "all" }: { mode?: PlacesMode }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [size, setSize] = useState(25);
  const [page, setPage] = useState(0);
  const [payload, setPayload] = useState<AdminWcoListPayload | undefined>();
  const [selected, setSelected] = useState<AdminWcoItem | undefined>();
  const [placeDetails, setPlaceDetails] = useState<AdminPlaceDetails | undefined>();
  const [placeLoading, setPlaceLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [typesInput, setTypesInput] = useState("");
  const [status, setStatus] = useState("Bereit.");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [markingClosed, setMarkingClosed] = useState(false);
  const [refreshingPlace, setRefreshingPlace] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [bulkTypesInput, setBulkTypesInput] = useState("");
  const [bulkCommentInput, setBulkCommentInput] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [duplicateListType, setDuplicateListType] = useState<"WCO" | "BUCKETLIST">("WCO");
  const [duplicateSize, setDuplicateSize] = useState(25);
  const [duplicatePage, setDuplicatePage] = useState(0);
  const [duplicateMaxGroups, setDuplicateMaxGroups] = useState(500);
  const [duplicatePayload, setDuplicatePayload] = useState<AdminWcoDuplicatePreviewPayload | undefined>();
  const [duplicateStatus, setDuplicateStatus] = useState("Bereit.");
  const [duplicateError, setDuplicateError] = useState("");
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const [shareQuery, setShareQuery] = useState("");
  const [shareType, setShareType] = useState<"ALL" | "COLLECTION" | "WECOMMENDATION">("ALL");
  const [shareOnlyExpired, setShareOnlyExpired] = useState(false);
  const [shareSize, setShareSize] = useState(25);
  const [sharePage, setSharePage] = useState(0);
  const [sharePayload, setSharePayload] = useState<AdminShareListPayload | undefined>();
  const [selectedShare, setSelectedShare] = useState<AdminShareItem | undefined>();
  const [shareInvalidAtInput, setShareInvalidAtInput] = useState("");
  const [shareStatus, setShareStatus] = useState("Bereit.");
  const [shareError, setShareError] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [collectionQuery, setCollectionQuery] = useState("");
  const [collectionType, setCollectionType] = useState<"ALL" | "WCO" | "BUCKETLIST">("ALL");
  const [collectionSize, setCollectionSize] = useState(25);
  const [collectionPage, setCollectionPage] = useState(0);
  const [collectionPayload, setCollectionPayload] = useState<AdminCollectionListPayload | undefined>();
  const [selectedCollection, setSelectedCollection] = useState<AdminCollectionItem | undefined>();
  const [collectionTitleInput, setCollectionTitleInput] = useState("");
  const [collectionCoverInput, setCollectionCoverInput] = useState("");
  const [collectionPlaceIdsInput, setCollectionPlaceIdsInput] = useState("");
  const [collectionEditType, setCollectionEditType] = useState<"WCO" | "BUCKETLIST">("WCO");
  const [collectionStatus, setCollectionStatus] = useState("Bereit.");
  const [collectionError, setCollectionError] = useState("");
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionSaving, setCollectionSaving] = useState(false);
  const showWco = mode === "all" || mode === "wecommendations";
  const showShares = mode === "all" || mode === "shares";
  const showCollections = mode === "all" || mode === "collections";

  const loadPage = async (nextPage = page, nextSize = size) => {
    setLoading(true);
    setError("");
    setStatus("Lade Wecommendations...");
    try {
      const response = await listAdminWco(token, {
        page: nextPage,
        size: nextSize,
        query: query || undefined
      });
      const data = normalizeResponseData(response);
      setPayload(data);
      setPage(data?.page ?? nextPage);
      if (!selected?.id) {
        setSelectedRows({});
      } else {
        setSelectedRows((prev) => {
          const next: Record<string, boolean> = {};
          Object.entries(prev).forEach(([id, value]) => {
            if (!value) return;
            if (data?.items?.some((item) => item.id === id)) {
              next[id] = true;
            }
          });
          return next;
        });
      }
      const selectedId = selected?.id;
      const matching = data?.items?.find((item) => item.id === selectedId);
      if (matching) {
        setSelected(matching);
        setComment(matching.userComment || "");
        setTypesInput((matching.placeType || []).join(", "));
        if (matching.placeId) {
          void loadPlace(matching.placeId, false);
        }
      }
      setStatus(`${data?.items?.length ?? 0} Einträge geladen.`);
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicates = async (nextPage = duplicatePage, nextSize = duplicateSize) => {
    setDuplicateLoading(true);
    setDuplicateError("");
    setDuplicateStatus("Lade Duplikat-Gruppen...");
    try {
      const response = await listAdminWcoDuplicates(token, {
        page: nextPage,
        size: nextSize,
        listType: duplicateListType
      });
      const data = normalizeResponseData(response);
      setDuplicatePayload(data);
      setDuplicatePage(data?.page ?? nextPage);
      setDuplicateStatus(
        `${data?.groups?.length ?? 0} Gruppen geladen. Doppelte Zeilen auf Seite: ${data?.duplicateRowsInPage ?? 0}.`
      );
    } catch (e) {
      setDuplicateError(apiErrorMessage(e));
      setDuplicateStatus("Fehler beim Laden der Duplikate.");
    } finally {
      setDuplicateLoading(false);
    }
  };

  const runDuplicateCleanup = async (dryRun: boolean) => {
    if (duplicateMaxGroups < 1) {
      setDuplicateStatus("maxGroups muss mindestens 1 sein.");
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        "Duplikate wirklich anwenden und löschen? Dieser Vorgang kann nicht rückgängig gemacht werden."
      );
      if (!ok) return;
    }

    setDuplicateActionLoading(true);
    setDuplicateError("");
    setDuplicateStatus(dryRun ? "Dry Run läuft..." : "Apply läuft...");
    try {
      const response = await cleanupAdminWcoDuplicates(token, {
        dryRun,
        maxGroups: duplicateMaxGroups,
        listType: duplicateListType
      });
      const data = normalizeResponseData(response);
      const duplicateRows = (data?.groups || []).reduce(
        (sum, group) => sum + (group.duplicateEntries?.length || 0),
        0
      );
      if (dryRun) {
        setDuplicateStatus(
          `Dry Run fertig: ${data?.affectedGroups ?? 0} Gruppen, ${duplicateRows} potenzielle Löschungen.`
        );
      } else {
        setDuplicateStatus(
          `Apply fertig: ${data?.affectedGroups ?? 0} Gruppen bereinigt, ${data?.deletedRows ?? duplicateRows} Zeilen gelöscht.`
        );
      }
      await loadDuplicates(duplicatePage, duplicateSize);
      if (!dryRun) {
        await loadPage(page, size);
      }
    } catch (e) {
      setDuplicateError(apiErrorMessage(e));
      setDuplicateStatus(dryRun ? "Dry Run fehlgeschlagen." : "Apply fehlgeschlagen.");
    } finally {
      setDuplicateActionLoading(false);
    }
  };

  useEffect(() => {
    if (showWco) {
      void loadPage(0, size);
    }
    if (showShares) {
      void loadShares(0, shareSize);
    }
    if (showCollections) {
      void loadCollections(0, collectionSize);
    }
  }, []);

  const loadPlace = async (placeId: string, forced = false) => {
    if (!placeId) {
      setPlaceDetails(undefined);
      return;
    }
    setPlaceLoading(true);
    try {
      const details = await getPlaceDetails(token, placeId, forced);
      setPlaceDetails(details);
    } catch {
      setPlaceDetails(undefined);
    } finally {
      setPlaceLoading(false);
    }
  };

  const loadShares = async (nextPage = sharePage, nextSize = shareSize) => {
    setShareLoading(true);
    setShareError("");
    setShareStatus("Lade Shares...");
    try {
      const response = await listAdminShares(token, {
        page: nextPage,
        size: nextSize,
        query: shareQuery || undefined,
        shareType: shareType === "ALL" ? undefined : shareType,
        onlyExpired: shareOnlyExpired
      });
      const data = normalizeResponseData(response);
      setSharePayload(data);
      setSharePage(data?.page ?? nextPage);
      if (selectedShare?.id) {
        const matching = data?.items?.find((item) => item.id === selectedShare.id);
        if (matching) {
          setSelectedShare(matching);
          setShareInvalidAtInput(toDateTimeLocalInput(matching.invalidAt));
        }
      }
      setShareStatus(`${data?.items?.length ?? 0} Shares geladen.`);
    } catch (e) {
      setShareError(apiErrorMessage(e));
      setShareStatus("Fehler beim Laden der Shares.");
    } finally {
      setShareLoading(false);
    }
  };

  const selectShare = (item: AdminShareItem) => {
    setSelectedShare(item);
    setShareInvalidAtInput(toDateTimeLocalInput(item.invalidAt));
    setShareStatus("Share ausgewählt.");
  };

  const loadCollections = async (nextPage = collectionPage, nextSize = collectionSize) => {
    setCollectionLoading(true);
    setCollectionError("");
    setCollectionStatus("Lade Collections...");
    try {
      const response = await listAdminCollections(token, {
        page: nextPage,
        size: nextSize,
        query: collectionQuery || undefined,
        type: collectionType === "ALL" ? undefined : collectionType
      });
      const data = normalizeResponseData(response);
      setCollectionPayload(data);
      setCollectionPage(data?.page ?? nextPage);
      if (selectedCollection?.id) {
        const matching = data?.items?.find((item) => item.id === selectedCollection.id);
        if (matching) {
          setSelectedCollection(matching);
          setCollectionTitleInput(matching.title || "");
          setCollectionCoverInput(matching.coverImage || "");
          setCollectionPlaceIdsInput((matching.placeIds || []).join(", "));
          setCollectionEditType((matching.type as "WCO" | "BUCKETLIST") || "WCO");
        }
      }
      setCollectionStatus(`${data?.items?.length ?? 0} Collections geladen.`);
    } catch (e) {
      setCollectionError(apiErrorMessage(e));
      setCollectionStatus("Fehler beim Laden der Collections.");
    } finally {
      setCollectionLoading(false);
    }
  };

  const selectCollection = (item: AdminCollectionItem) => {
    setSelectedCollection(item);
    setCollectionTitleInput(item.title || "");
    setCollectionCoverInput(item.coverImage || "");
    setCollectionPlaceIdsInput((item.placeIds || []).join(", "));
    setCollectionEditType((item.type as "WCO" | "BUCKETLIST") || "WCO");
    setCollectionStatus("Collection ausgewählt.");
  };

  const saveCollection = async () => {
    if (!selectedCollection?.id) {
      setCollectionStatus("Keine Collection ausgewählt.");
      return;
    }
    const title = collectionTitleInput.trim();
    const placeIds = parsePlaceIds(collectionPlaceIdsInput);
    if (!title) {
      setCollectionStatus("Titel darf nicht leer sein.");
      return;
    }
    if (!placeIds.length) {
      setCollectionStatus("Mindestens eine Place ID ist erforderlich.");
      return;
    }
    setCollectionSaving(true);
    setCollectionError("");
    setCollectionStatus("Collection wird gespeichert...");
    try {
      await updateAdminCollection(token, selectedCollection.id, {
        title,
        coverImage: collectionCoverInput.trim() || null,
        type: collectionEditType,
        placeIds
      });
      await loadCollections(collectionPage, collectionSize);
      setCollectionStatus("Collection gespeichert.");
    } catch (e) {
      setCollectionError(apiErrorMessage(e));
      setCollectionStatus("Collection-Update fehlgeschlagen.");
    } finally {
      setCollectionSaving(false);
    }
  };

  const totalPages = Math.max(1, payload?.totalPages ?? 1);
  const duplicateTotalPages = Math.max(1, duplicatePayload?.totalPages ?? 1);
  const duplicateGroups = duplicatePayload?.groups || [];
  const shareTotalPages = Math.max(1, sharePayload?.totalPages ?? 1);
  const collectionTotalPages = Math.max(1, collectionPayload?.totalPages ?? 1);
  const pageItems = payload?.items || [];
  const shareLandingBase =
    window.location.hostname.includes("staging")
      ? "https://staging.wecommend.app/share/"
      : "https://wecommend.app/share/";
  const selectedIds = pageItems.filter((item) => selectedRows[item.id]).map((item) => item.id);
  const allSelectedOnPage = pageItems.length > 0 && pageItems.every((item) => selectedRows[item.id]);
  const pageTitle =
    mode === "wecommendations"
      ? "Wecommendations"
      : mode === "collections"
        ? "Collections"
        : mode === "shares"
          ? "Shares"
          : "Wecommendations & Places";
  const pageSubtitle =
    mode === "wecommendations"
      ? "Chronologische WCO-Liste mit Edit von Kommentar und Types."
      : mode === "collections"
        ? "Collections durchsuchen und direkt bearbeiten."
        : mode === "shares"
          ? "Share-Links, Klickzahlen und Ablauf/Deaktivierung administrieren."
          : "Moderation, Status-Checks und Place-Editing.";

  const toggleAllOnPage = (checked: boolean) => {
    const next: Record<string, boolean> = { ...selectedRows };
    pageItems.forEach((item) => {
      next[item.id] = checked;
    });
    setSelectedRows(next);
  };

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedRows((prev) => ({ ...prev, [id]: checked }));
  };

  const selectItem = (item: AdminWcoItem) => {
    setSelected(item);
    setComment(item.userComment || "");
    setTypesInput((item.placeType || []).join(", "));
    setStatus("Bereit.");
    if (item.placeId) {
      void loadPlace(item.placeId, false);
    } else {
      setPlaceDetails(undefined);
    }
  };

  const saveSelected = async () => {
    if (!selected?.id) {
      setStatus("Kein Eintrag ausgewählt.");
      return;
    }
    const newComment = comment.trim();
    const newTypes = parseTypes(typesInput);
    if (!newComment) {
      setStatus("Kommentar darf nicht leer sein.");
      return;
    }
    if (newComment.length > 300) {
      setStatus("Kommentar darf maximal 300 Zeichen haben.");
      return;
    }
    if (newTypes.length === 0) {
      setStatus("Mindestens ein Type ist erforderlich.");
      return;
    }

    setSaving(true);
    setError("");
    setStatus("Speichern...");
    try {
      await updateAdminWco(token, selected.id, {
        userComment: newComment,
        placeType: newTypes
      });
      setStatus("Erfolgreich gespeichert.");
      await loadPage(page, size);
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const refreshSelectedPlace = async () => {
    if (!selected?.placeId) {
      setStatus("Kein Place ausgewählt.");
      return;
    }
    setRefreshingPlace(true);
    setStatus("Place Refresh läuft...");
    setError("");
    try {
      await loadPlace(selected.placeId, true);
      await loadPage(page, size);
      setStatus("Place über Google aktualisiert.");
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Place Refresh fehlgeschlagen.");
    } finally {
      setRefreshingPlace(false);
    }
  };

  const markSelectedPlaceClosed = async () => {
    if (!selected?.placeId) {
      setStatus("Kein Place ausgewählt.");
      return;
    }
    const ok = window.confirm(
      "Place wirklich als geschlossen markieren? Das wirkt sofort in der App."
    );
    if (!ok) return;

    setMarkingClosed(true);
    setError("");
    setStatus("Place wird als geschlossen markiert...");
    try {
      await markAdminWcoPlaceClosed(token, selected.placeId);
      await loadPlace(selected.placeId, false);
      await loadPage(page, size);
      setStatus("Place als geschlossen markiert.");
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Place konnte nicht als geschlossen markiert werden.");
    } finally {
      setMarkingClosed(false);
    }
  };

  const saveShareExpiry = async () => {
    if (!selectedShare?.id) {
      setShareStatus("Kein Share ausgewählt.");
      return;
    }
    setShareSaving(true);
    setShareError("");
    setShareStatus("Share wird gespeichert...");
    try {
      await updateAdminShare(token, selectedShare.id, {
        disable: false,
        invalidAt: fromDateTimeLocalInput(shareInvalidAtInput)
      });
      await loadShares(sharePage, shareSize);
      setShareStatus("Share erfolgreich aktualisiert.");
    } catch (e) {
      setShareError(apiErrorMessage(e));
      setShareStatus("Share-Update fehlgeschlagen.");
    } finally {
      setShareSaving(false);
    }
  };

  const disableShareNow = async () => {
    if (!selectedShare?.id) {
      setShareStatus("Kein Share ausgewählt.");
      return;
    }
    setShareSaving(true);
    setShareError("");
    setShareStatus("Share wird deaktiviert...");
    try {
      await updateAdminShare(token, selectedShare.id, { disable: true });
      await loadShares(sharePage, shareSize);
      setShareStatus("Share deaktiviert.");
    } catch (e) {
      setShareError(apiErrorMessage(e));
      setShareStatus("Share-Deaktivierung fehlgeschlagen.");
    } finally {
      setShareSaving(false);
    }
  };

  const applyBulkUpdate = async () => {
    if (selectedIds.length === 0) {
      setStatus("Keine WCO-Zeilen für Bulk-Update ausgewählt.");
      return;
    }
    const parsedTypes = parseTypes(bulkTypesInput);
    const parsedComment = bulkCommentInput.trim();
    if (!parsedTypes.length && !parsedComment) {
      setStatus("Bitte für Bulk-Update mindestens Kommentar oder Types setzen.");
      return;
    }
    if (parsedComment.length > 300) {
      setStatus("Bulk-Kommentar darf maximal 300 Zeichen haben.");
      return;
    }

    setBulkLoading(true);
    setError("");
    setStatus("Bulk-Update läuft...");
    try {
      const response = await updateAdminWcoBulk(token, {
        placeListIds: selectedIds,
        userComment: parsedComment || undefined,
        placeType: parsedTypes.length ? parsedTypes : undefined
      });
      const data = normalizeResponseData(response);
      setStatus(`Bulk erfolgreich: ${data?.updated ?? 0}/${data?.requested ?? selectedIds.length} aktualisiert.`);
      if ((data?.failedIds?.length || 0) > 0) {
        setError(`Fehlgeschlagen für ${data?.failedIds?.length} IDs.`);
      }
      await loadPage(page, size);
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Bulk-Update fehlgeschlagen.");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <section>
      <div className="view-headline">
        <h1>{pageTitle}</h1>
        <p className="muted">{pageSubtitle}</p>
      </div>

      {showWco ? (
      <div className="card">
        <div className="places-toolbar">
          <div>
            <label>Suche</label>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Place, User, Kommentar, Place ID"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void loadPage(0, size);
                }
              }}
            />
          </div>
          <div>
            <label>Size</label>
            <select
              value={size}
              onChange={async (event) => {
                const nextSize = Number(event.target.value);
                setSize(nextSize);
                await loadPage(0, nextSize);
              }}
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <button type="button" onClick={() => void loadPage(0, size)} disabled={loading}>
            {loading ? "Lade..." : "Laden"}
          </button>
        </div>

        <div className="section-header">
          <div className="muted">{status}</div>
          <div className="pager">
            <button
              type="button"
              disabled={page <= 0}
              onClick={async () => {
                const next = Math.max(0, page - 1);
                setPage(next);
                await loadPage(next, size);
              }}
            >
              ‹
            </button>
            <span className="pager-info">
              Seite {Math.min(page + 1, totalPages)}/{totalPages} · Total {payload?.total ?? 0}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={async () => {
                const next = page + 1;
                setPage(next);
                await loadPage(next, size);
              }}
            >
              ›
            </button>
          </div>
        </div>

        <div className="bulk-editor-row">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={allSelectedOnPage}
              onChange={(event) => toggleAllOnPage(event.target.checked)}
            />
            Alle auf Seite auswählen
          </label>
          <input
            value={bulkTypesInput}
            onChange={(event) => setBulkTypesInput(event.target.value)}
            placeholder="Bulk Types (kommagetrennt)"
          />
          <input
            value={bulkCommentInput}
            onChange={(event) => setBulkCommentInput(event.target.value)}
            placeholder="Bulk Kommentar (optional)"
          />
          <button type="button" className="primary" disabled={bulkLoading} onClick={() => void applyBulkUpdate()}>
            {bulkLoading ? "Bulk..." : `Bulk Update (${selectedIds.length})`}
          </button>
        </div>

        <div className="duplicate-tools">
          <div>
            <label>Duplikat-Type</label>
            <select
              value={duplicateListType}
              onChange={(event) => {
                const next = event.target.value as "WCO" | "BUCKETLIST";
                setDuplicateListType(next);
                setDuplicatePage(0);
                setDuplicatePayload(undefined);
              }}
            >
              <option value="WCO">WCO</option>
              <option value="BUCKETLIST">BUCKETLIST</option>
            </select>
          </div>
          <div>
            <label>Preview Size</label>
            <select
              value={duplicateSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                setDuplicateSize(next);
              }}
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>maxGroups (Cleanup)</label>
            <input
              type="number"
              min={1}
              max={5000}
              value={duplicateMaxGroups}
              onChange={(event) => setDuplicateMaxGroups(Number(event.target.value))}
            />
          </div>
          <button type="button" disabled={duplicateLoading} onClick={() => void loadDuplicates(0, duplicateSize)}>
            {duplicateLoading ? "Lade..." : "Duplikate laden"}
          </button>
          <button
            type="button"
            disabled={duplicateActionLoading}
            onClick={() => void runDuplicateCleanup(true)}
          >
            {duplicateActionLoading ? "Läuft..." : "Cleanup Dry Run"}
          </button>
          <button
            type="button"
            className="primary"
            disabled={duplicateActionLoading}
            onClick={() => void runDuplicateCleanup(false)}
          >
            {duplicateActionLoading ? "Läuft..." : "Cleanup Apply"}
          </button>
        </div>

        <div className="section-header">
          <div className="muted">{duplicateStatus}</div>
          <div className="pager">
            <button
              type="button"
              disabled={duplicatePage <= 0}
              onClick={async () => {
                const next = Math.max(0, duplicatePage - 1);
                setDuplicatePage(next);
                await loadDuplicates(next, duplicateSize);
              }}
            >
              ‹
            </button>
            <span className="pager-info">
              Duplikate {Math.min(duplicatePage + 1, duplicateTotalPages)}/{duplicateTotalPages} · Gruppen{" "}
              {duplicatePayload?.totalGroups ?? 0}
            </span>
            <button
              type="button"
              disabled={duplicatePage >= duplicateTotalPages - 1}
              onClick={async () => {
                const next = duplicatePage + 1;
                setDuplicatePage(next);
                await loadDuplicates(next, duplicateSize);
              }}
            >
              ›
            </button>
          </div>
        </div>

        <div className="table-wrap duplicate-table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Place</th>
                <th>Keep (ältester)</th>
                <th>Duplikate</th>
              </tr>
            </thead>
            <tbody>
              {duplicateGroups.map((group) => (
                <tr key={`${group.userId || "?"}:${group.placeId || "?"}`}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        if (!group.userId) return;
                        navigate(`/users/${encodeURIComponent(group.userId)}`);
                      }}
                      disabled={!group.userId}
                    >
                      {group.userName || group.userId || "-"}
                    </button>
                    <div className="muted">{group.userEmail || "-"}</div>
                  </td>
                  <td>
                    <strong>{group.placeName || "-"}</strong>
                    <div className="muted">{group.placeAddress || "-"}</div>
                    <div className="muted">{group.placeId || "-"}</div>
                  </td>
                  <td>
                    <div className="muted">ID: {group.keepEntry?.id || "-"}</div>
                    <div>{formatDate(group.keepEntry?.createdDate)}</div>
                    <div className="place-comment">{group.keepEntry?.userComment || "-"}</div>
                  </td>
                  <td>
                    <div>
                      <strong>{group.duplicateEntries?.length || 0}</strong> von {group.totalEntries || 0}
                    </div>
                    <div className="duplicate-chip-list">
                      {(group.duplicateEntries || []).slice(0, 5).map((entry) => (
                        <span className="item-tag" key={entry.id || `${entry.createdDate}-${entry.lastModifiedDate}`}>
                          {formatDate(entry.createdDate)} · {entry.id}
                        </span>
                      ))}
                      {(group.duplicateEntries?.length || 0) > 5 ? (
                        <span className="item-tag">+{(group.duplicateEntries?.length || 0) - 5} weitere</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {duplicateGroups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="muted">
                    Keine Duplikat-Gruppen geladen.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {duplicateError ? <div className="error-box">{duplicateError}</div> : null}
        {error ? <div className="error-box">{error}</div> : null}

        <div className="places-layout">
          <div className="table-wrap places-table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Updated</th>
                  <th>User</th>
                  <th>Place</th>
                  <th>Kommentar</th>
                  <th>Types</th>
                </tr>
              </thead>
              <tbody>
                {(payload?.items || []).map((item) => (
                  <tr
                    key={item.id}
                    className={`place-row ${selected?.id === item.id ? "active" : ""}`}
                    onClick={() => selectItem(item)}
                  >
                    <td onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!selectedRows[item.id]}
                        onChange={(event) => toggleRow(item.id, event.target.checked)}
                      />
                    </td>
                    <td>{formatDate(item.lastModifiedDate || item.createdDate)}</td>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!item.userId) return;
                          navigate(`/users/${encodeURIComponent(item.userId)}`);
                        }}
                        disabled={!item.userId}
                      >
                        {item.userName || "-"}
                      </button>
                      <div className="muted">{item.userEmail || item.userId || "-"}</div>
                    </td>
                    <td>
                      <strong>{item.placeName || "-"}</strong>
                      <div className="muted">{item.placeAddress || "-"}</div>
                      <div className="muted">{item.placeId || "-"}</div>
                    </td>
                    <td className="place-comment">{item.userComment || "-"}</td>
                    <td>
                      <div className="item-tags">
                        {(item.placeType || []).map((type) => (
                          <span key={type} className="item-tag">
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {(payload?.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      Keine Wecommendations gefunden.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="card place-editor-card">
            <label>Weco bearbeiten</label>
            {!selected ? (
              <div className="muted">Bitte links einen Eintrag auswählen.</div>
            ) : (
              <>
                <div className="place-meta">
                  <div>
                    <strong>User:</strong> {selected.userName || "-"} ({selected.userEmail || "-"})
                  </div>
                  <div>
                    <strong>Place:</strong> {selected.placeName || "-"} · {selected.placeAddress || "-"}
                  </div>
                  <div>
                    <strong>IDs:</strong> placeList={selected.id} · placeId={selected.placeId || "-"}
                  </div>
                  <div>
                    <strong>Updated:</strong> {formatDate(selected.lastModifiedDate)} · <strong>Created:</strong>{" "}
                    {formatDate(selected.createdDate)}
                  </div>
                </div>

                <label>Kommentar</label>
                <textarea value={comment} rows={5} onChange={(event) => setComment(event.target.value)} />

                <label>Place Types (kommagetrennt)</label>
                <input value={typesInput} onChange={(event) => setTypesInput(event.target.value)} />

                <div className="place-editor-actions">
                  <button
                    type="button"
                    disabled={refreshingPlace || !selected.placeId}
                    onClick={() => void refreshSelectedPlace()}
                  >
                    {refreshingPlace ? "Refresh..." : "Place Refresh"}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={markingClosed || !selected.placeId}
                    onClick={() => void markSelectedPlaceClosed()}
                  >
                    {markingClosed ? "Markiere..." : "Als geschlossen markieren"}
                  </button>
                  {selected.placeId ? (
                    <a
                      className="button-link"
                      href={`https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(selected.placeId)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      In Google Maps
                    </a>
                  ) : null}
                  <button type="button" className="primary" disabled={saving} onClick={() => void saveSelected()}>
                    {saving ? "Speichern..." : "Speichern"}
                  </button>
                </div>
                <div className="place-detail-card">
                  <div className="place-detail-header">Place Details</div>
                  {placeLoading ? <div className="muted">Lade Place Details...</div> : null}
                  {!placeLoading && !placeDetails ? (
                    <div className="muted">Keine Place-Details verfügbar.</div>
                  ) : null}
                  {!placeLoading && placeDetails ? (
                    <div className="place-detail-grid">
                      <div>
                        <div className="profile-label">Name</div>
                        <div className="profile-value">{placeDetails.name || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Business Status</div>
                        <div className="profile-value">{placeDetails.businessStatus || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Adresse</div>
                        <div className="profile-value">{placeDetails.formattedAddress || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Telefon</div>
                        <div className="profile-value">{placeDetails.formattedPhone || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Website</div>
                        <div className="profile-value">{placeDetails.website || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Location</div>
                        <div className="profile-value">{placeDetails.location || "-"}</div>
                      </div>
                      <div>
                        <div className="profile-label">Types</div>
                        <div className="item-tags">
                          {(placeDetails.types || []).map((type) => (
                            <span key={type} className="item-tag">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {!placeLoading && placeDetails?.photos?.length ? (
                    <img
                      src={placeDetails.photos[0]}
                      alt={placeDetails.name || "place"}
                      className="place-detail-photo"
                    />
                  ) : null}
                </div>
                <div className="action-status info">{status}</div>
              </>
            )}
          </div>
        </div>
      </div>
      ) : null}

      {showShares ? (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="view-headline">
          <h2>Shares</h2>
          <p className="muted">Share-Links, Klickzahlen und Ablauf/Deaktivierung administrieren.</p>
        </div>

        <div className="places-toolbar">
          <div>
            <label>Suche</label>
            <input
              value={shareQuery}
              onChange={(event) => setShareQuery(event.target.value)}
              placeholder="Link, Collection-Titel, Place-ID"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void loadShares(0, shareSize);
                }
              }}
            />
          </div>
          <div>
            <label>Type</label>
            <select value={shareType} onChange={(event) => setShareType(event.target.value as "ALL" | "COLLECTION" | "WECOMMENDATION")}>
              <option value="ALL">Alle</option>
              <option value="COLLECTION">Collection</option>
              <option value="WECOMMENDATION">Wecommendation</option>
            </select>
          </div>
          <div className="checkbox-row">
            <label>Nur abgelaufen</label>
            <input
              type="checkbox"
              checked={shareOnlyExpired}
              onChange={(event) => setShareOnlyExpired(event.target.checked)}
            />
          </div>
          <div>
            <label>Size</label>
            <select
              value={shareSize}
              onChange={async (event) => {
                const nextSize = Number(event.target.value);
                setShareSize(nextSize);
                await loadShares(0, nextSize);
              }}
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <button type="button" onClick={() => void loadShares(0, shareSize)} disabled={shareLoading}>
            {shareLoading ? "Lade..." : "Laden"}
          </button>
        </div>

        <div className="section-header">
          <div className="muted">{shareStatus}</div>
          <div className="pager">
            <button
              type="button"
              disabled={sharePage <= 0}
              onClick={async () => {
                const next = Math.max(0, sharePage - 1);
                setSharePage(next);
                await loadShares(next, shareSize);
              }}
            >
              ‹
            </button>
            <span className="pager-info">
              Seite {Math.min(sharePage + 1, shareTotalPages)}/{shareTotalPages} · Total {sharePayload?.total ?? 0}
            </span>
            <button
              type="button"
              disabled={sharePage >= shareTotalPages - 1}
              onClick={async () => {
                const next = sharePage + 1;
                setSharePage(next);
                await loadShares(next, shareSize);
              }}
            >
              ›
            </button>
          </div>
        </div>

        {shareError ? <div className="error-box">{shareError}</div> : null}

        <div className="places-layout">
          <div className="table-wrap places-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>Type</th>
                  <th>Titel / Place</th>
                  <th>Link</th>
                  <th>Klicks</th>
                  <th>Expiry</th>
                </tr>
              </thead>
              <tbody>
                {(sharePayload?.items || []).map((item) => (
                  <tr
                    key={item.id}
                    className={`place-row ${selectedShare?.id === item.id ? "active" : ""}`}
                    onClick={() => selectShare(item)}
                  >
                    <td>{formatDate(item.lastModifiedDate || item.createdDate)}</td>
                    <td>
                      <strong>{item.shareType || "-"}</strong>
                    </td>
                    <td>
                      <strong>{item.collectionTitle || item.placeName || "-"}</strong>
                      <div className="muted">
                        {item.collectionId || item.placeId || "-"}
                      </div>
                      <div className="muted">
                        Owner: {item.createdByName || item.createdByUserId || "-"}
                      </div>
                    </td>
                    <td className="place-comment">{item.link || "-"}</td>
                    <td>
                      <strong>{item.openCount ?? 0}</strong>
                      <div className="muted">last: {formatDate(item.lastOpenedAt)}</div>
                    </td>
                    <td>
                      <span className={`item-tag ${item.expired ? "" : "ok"}`}>
                        {item.expired ? "abgelaufen" : "aktiv"}
                      </span>
                      <div className="muted">{formatDate(item.invalidAt)}</div>
                    </td>
                  </tr>
                ))}
                {(sharePayload?.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      Keine Shares gefunden.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="card place-editor-card">
            <label>Share bearbeiten</label>
            {!selectedShare ? (
              <div className="muted">Bitte links einen Share auswählen.</div>
            ) : (
              <>
                <div className="place-meta">
                  <div>
                    <strong>Type:</strong> {selectedShare.shareType || "-"}
                  </div>
                  <div>
                    <strong>Objekt:</strong> {selectedShare.collectionTitle || selectedShare.placeName || "-"}
                  </div>
                  <div>
                    <strong>IDs:</strong> share={selectedShare.id} · collection={selectedShare.collectionId || "-"} · place=
                    {selectedShare.placeId || "-"}
                  </div>
                  <div>
                    <strong>Klicks:</strong> {selectedShare.openCount ?? 0} · <strong>Last open:</strong>{" "}
                    {formatDate(selectedShare.lastOpenedAt)}
                  </div>
                  <div>
                    <strong>Link:</strong>{" "}
                    <a
                      href={`${shareLandingBase}${selectedShare.link || ""}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {selectedShare.link || "-"}
                    </a>
                  </div>
                </div>

                <label>Expires At</label>
                <input
                  type="datetime-local"
                  value={shareInvalidAtInput}
                  onChange={(event) => setShareInvalidAtInput(event.target.value)}
                />

                <div className="place-editor-actions">
                  <button type="button" className="danger" disabled={shareSaving} onClick={() => void disableShareNow()}>
                    {shareSaving ? "..." : "Jetzt deaktivieren"}
                  </button>
                  <button type="button" className="primary" disabled={shareSaving} onClick={() => void saveShareExpiry()}>
                    {shareSaving ? "Speichern..." : "Expiry speichern / reaktivieren"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      ) : null}

      {showCollections ? (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="view-headline">
          <h2>Collections</h2>
          <p className="muted">Collections durchsuchen und direkt bearbeiten.</p>
        </div>

        <div className="places-toolbar">
          <div>
            <label>Suche</label>
            <input
              value={collectionQuery}
              onChange={(event) => setCollectionQuery(event.target.value)}
              placeholder="Titel, User, E-Mail, Place ID"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void loadCollections(0, collectionSize);
                }
              }}
            />
          </div>
          <div>
            <label>Type</label>
            <select
              value={collectionType}
              onChange={(event) => setCollectionType(event.target.value as "ALL" | "WCO" | "BUCKETLIST")}
            >
              <option value="ALL">Alle</option>
              <option value="WCO">WCO</option>
              <option value="BUCKETLIST">BUCKETLIST</option>
            </select>
          </div>
          <div>
            <label>Size</label>
            <select
              value={collectionSize}
              onChange={async (event) => {
                const nextSize = Number(event.target.value);
                setCollectionSize(nextSize);
                await loadCollections(0, nextSize);
              }}
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <button type="button" onClick={() => void loadCollections(0, collectionSize)} disabled={collectionLoading}>
            {collectionLoading ? "Lade..." : "Laden"}
          </button>
        </div>

        <div className="section-header">
          <div className="muted">{collectionStatus}</div>
          <div className="pager">
            <button
              type="button"
              disabled={collectionPage <= 0}
              onClick={async () => {
                const next = Math.max(0, collectionPage - 1);
                setCollectionPage(next);
                await loadCollections(next, collectionSize);
              }}
            >
              ‹
            </button>
            <span className="pager-info">
              Seite {Math.min(collectionPage + 1, collectionTotalPages)}/{collectionTotalPages} · Total{" "}
              {collectionPayload?.total ?? 0}
            </span>
            <button
              type="button"
              disabled={collectionPage >= collectionTotalPages - 1}
              onClick={async () => {
                const next = collectionPage + 1;
                setCollectionPage(next);
                await loadCollections(next, collectionSize);
              }}
            >
              ›
            </button>
          </div>
        </div>

        {collectionError ? <div className="error-box">{collectionError}</div> : null}

        <div className="places-layout">
          <div className="table-wrap places-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Titel</th>
                  <th>Places</th>
                </tr>
              </thead>
              <tbody>
                {(collectionPayload?.items || []).map((item) => (
                  <tr
                    key={item.id}
                    className={`place-row ${selectedCollection?.id === item.id ? "active" : ""}`}
                    onClick={() => selectCollection(item)}
                  >
                    <td>{formatDate(item.lastModifiedDate || item.createdDate)}</td>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!item.userId) return;
                          navigate(`/users/${encodeURIComponent(item.userId)}`);
                        }}
                        disabled={!item.userId}
                      >
                        {item.userName || "-"}
                      </button>
                      <div className="muted">{item.userEmail || item.userId || "-"}</div>
                    </td>
                    <td>
                      <span className="item-tag ok">{item.type || "-"}</span>
                    </td>
                    <td>
                      <strong>{item.title || "-"}</strong>
                      <div className="muted">{item.id || "-"}</div>
                    </td>
                    <td>
                      <strong>{item.placesCount ?? 0}</strong>
                      <div className="muted">{(item.placeNames || []).slice(0, 2).join(" · ") || "-"}</div>
                    </td>
                  </tr>
                ))}
                {(collectionPayload?.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      Keine Collections gefunden.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="card place-editor-card">
            <label>Collection bearbeiten</label>
            {!selectedCollection ? (
              <div className="muted">Bitte links eine Collection auswählen.</div>
            ) : (
              <>
                <div className="place-meta">
                  <div>
                    <strong>User:</strong> {selectedCollection.userName || "-"} ({selectedCollection.userEmail || "-"})
                  </div>
                  <div>
                    <strong>ID:</strong> {selectedCollection.id}
                  </div>
                  <div>
                    <strong>Updated:</strong> {formatDate(selectedCollection.lastModifiedDate)} · <strong>Created:</strong>{" "}
                    {formatDate(selectedCollection.createdDate)}
                  </div>
                </div>

                {collectionCoverInput ? (
                  <img src={collectionCoverInput} alt={collectionTitleInput || "collection"} className="place-detail-photo" />
                ) : null}

                <label>Titel</label>
                <input value={collectionTitleInput} onChange={(event) => setCollectionTitleInput(event.target.value)} />

                <label>Type</label>
                <select
                  value={collectionEditType}
                  onChange={(event) => setCollectionEditType(event.target.value as "WCO" | "BUCKETLIST")}
                >
                  <option value="WCO">WCO</option>
                  <option value="BUCKETLIST">BUCKETLIST</option>
                </select>

                <label>Cover Image URL</label>
                <input value={collectionCoverInput} onChange={(event) => setCollectionCoverInput(event.target.value)} />

                <label>Place IDs (kommagetrennt oder Zeilenumbruch)</label>
                <textarea
                  value={collectionPlaceIdsInput}
                  rows={5}
                  onChange={(event) => setCollectionPlaceIdsInput(event.target.value)}
                />
                <div className="muted">Anzahl Place IDs: {parsePlaceIds(collectionPlaceIdsInput).length}</div>

                <div className="place-editor-actions">
                  <button type="button" className="primary" disabled={collectionSaving} onClick={() => void saveCollection()}>
                    {collectionSaving ? "Speichern..." : "Collection speichern"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      ) : null}
    </section>
  );
}
